
import Vue from "vue";
import { CanadidateReportInfo, TypedVue } from "@/store/types";
import { Component, Provide, Watch } from "vue-property-decorator";
import { Action } from "vuex-class";
import VueI18n from "vue-i18n";
import adminService from "@/services/adminService";
import { Actions } from "@/store/admin/adminActions";
import { VuexModules } from "@/store";
import { Assessment, Benchmark, ElraReportData, Project, Resource } from "@/store/admin/adminTypes";
import { AxiosError } from "axios";
import { Trait, TRAITS } from "@/lib/consts";
import ElraReport8 from "@/components/reports/ElraReport8.vue";
import { ComparisonAssessmentItem, ComparisonBenchmarkItem } from "@/components/admin/ComparisonComponents/ComparisonRadarChartComponent.vue";
import { ColorMap } from "@/lib/charts/HighChartThemes";

const namespace = VuexModules.ADMIN;

interface SelectItem {
  text: string;
  value: string | null;
}

interface AssessmentSelectItem extends SelectItem {
  hasReport?: boolean;
  isComplete?: boolean;
  name: string;
}

@Component({
  components: {
    ElraReport8,
  },
})
export default class GenerateELRAReport extends TypedVue {
  @Action(Actions.GET_LITERALS, { namespace })
  getLiterals!: ({ module, version }: { module: string; version: string }) => Promise<Resource>;

  @Provide("$i18n") readonly reportI18n: VueI18n = new VueI18n({ locale: "en" });

  readonly reportType = "elra";
  readonly reportVersion = "8.0";
  readonly useCustomScoring = false;

  selectedOrg: string | null = null;
  selectedProject: string | null = null;
  selectedAssessment: string | null = null;

  showIncompleteOnly = false;
  generateReport = false;
  orgsLoading = false;
  projectsLoading = false;
  assessmentsLoading = false;
  generating = false;
  publishing = false;
  sendEmail = false;
  errorMsgs: string[] = [];

  orgs: SelectItem[] = [];
  projects: SelectItem[] = [];
  fullProjects: Project[] = [];
  assessments: AssessmentSelectItem[] = [];
  fullAssessments: Assessment[] = [];
  benchmarks: Benchmark[] = [];

  reportData: CanadidateReportInfo | null = null;
  comparisonCandidates: ComparisonAssessmentItem[] = [];
  comparisonBenchmarks: ComparisonBenchmarkItem[] = [];

  get loading(): boolean {
    return this.orgsLoading || this.projectsLoading || this.assessmentsLoading;
  }

  get filteredAssessments(): SelectItem[] {
    const assessmentFilter = (entry: AssessmentSelectItem) => {
      if (!entry.isComplete) {
        return false;
      }
      return this.showIncompleteOnly ? entry.hasReport === false : true;
    };
    if (this.selectedProject !== null) {
      const results = this.assessments.filter(assessmentFilter);
      if (results) {
        return results;
      }
    }
    return [];
  }

  get selectedAssessmentName() {
    const selected = this.assessments.find((a) => a.value === this.selectedAssessment);
    if (selected) {
      return selected.name;
    }
    return "";
  }

  get traitClassRanges(): Record<Trait, { low: number; high: number; lowerlow: number; mean: number; standardDeviation: number }> {
    const rootBenchmarkId = this.fullProjects.find((p) => p.projectId === this.selectedProject)?.rootBenchmarkId;
    const rootBenchmark = this.benchmarks.find((b) => b.benchmarkId === rootBenchmarkId);
    if (rootBenchmark && rootBenchmark.traitCutoffs) {
      return rootBenchmark.traitCutoffs as Record<Trait, { low: number; high: number; lowerlow: number; mean: number; standardDeviation: number }>;
    }
    return {} as Record<Trait, { low: number; high: number; lowerlow: number; mean: number; standardDeviation: number }>;
  }

  get projectTheme(): string {
    return this.fullProjects.find((p) => p.projectId === this.selectedProject)?.theme ?? "default-ta";
  }

  @Watch("selectedOrg")
  onOrgSelect(newOrg: string): void {
    this.selectedProject = null;
    this.generateReport = false;
    if (newOrg) {
      this.getProjects(newOrg).catch((err: AxiosError) => this.errorMsgs.push(err.message));
    }
  }

  @Watch("selectedProject")
  onProjectSelect(newProj: string): void {
    this.selectedAssessment = null;
    this.generateReport = false;
    if (newProj) {
      this.getAssessments(newProj).catch((err: AxiosError) => this.errorMsgs.push(err.message));
    }
  }

  @Watch("selectedAssessment")
  onAssessmentSelect(): void {
    this.generateReport = false;
  }

  mounted(): void {
    this.getOrgs().catch((err: AxiosError) => this.errorMsgs.push(err.message));
  }

  async getOrgs(): Promise<void> {
    this.orgsLoading = true;
    const organizations = (await adminService.getOrganizations()).map((org) => {
      return { text: org.displayName, value: org.id };
    });
    this.orgs = organizations;
    this.orgsLoading = false;
  }

  async getProjects(selectedOrg: string): Promise<void> {
    this.projectsLoading = true;

    await adminService.getProjects(selectedOrg, null).then((res) => {
      const projects = res.map((p: Project) => {
        return { value: p.projectId, text: p.name };
      });
      this.projects = projects;
      this.fullProjects = res;
    }),
      (this.projectsLoading = false);
  }

  async getAssessments(selectedProject: string): Promise<void> {
    this.assessmentsLoading = true;
    await adminService.getAssessments(selectedProject).then((res) => {
      this.fullAssessments = res.assessments;
      this.assessments = res.assessments.map((entry) => {
        return {
          value: entry.assessmentId,
          text: `${entry.assessmentId.substring(0, 4)} - ${entry.candidateName}`,
          hasReport: entry.hasReport,
          isComplete: entry.isComplete,
          name: entry.candidateName,
        };
      });
    });
    this.assessmentsLoading = false;
  }

  async getBenchmarks(): Promise<void> {
    if (this.selectedAssessment) {
      const projectId = this.fullAssessments.find((a) => a.assessmentId === this.selectedAssessment)?.project;
      this.benchmarks = await adminService.getProjectBenchmarks(projectId as string);
    }
  }

  handleGenerateReport(): void {
    if (this.selectedAssessment) {
      this.generating = true;
      this.errorMsgs = [];
      const assessmentId = this.selectedAssessment;

      const benchmarkRequest = this.getBenchmarks();
      const dataRequest = adminService.getReportTemplateData(assessmentId, this.useCustomScoring).then((data) => {
        data.reportVersion = this.reportVersion;
        data.type = this.reportType;
        this.reportData = {
          data,
          id: "",
          readiness: data.readiness,
          type: this.reportType,
          version: this.reportVersion,
          projectId: "",
        };
        this.reportData.data.candidate_name = this.selectedAssessmentName;
      });
      const literalsRequest = this.getLiterals({ module: `report:${this.reportType}`, version: "8" }).then((reportLiterals) => {
        const adminMessages = (this.$i18n as VueI18n).getLocaleMessage("en");
        this.reportI18n.setLocaleMessage("en", { ...adminMessages, ...reportLiterals.dictionary });
      });

      Promise.all([dataRequest, benchmarkRequest, literalsRequest])
        .then(() => {
          this.buildRadarChartData();
        })
        .catch((err: AxiosError) => {
          if (err.response) {
            if ((err.response.data as any)?.detail) {
              this.errorMsgs.push(`Error: ${(err.response.data as any).detail}`);
            } else {
              this.errorMsgs.push(err.response.data as any);
            }
          } else {
            this.errorMsgs.push(err.message);
          }
        })
        .finally(() => {
          this.generating = false;
          this.generateReport = true;
        });
    }
  }

  buildRadarChartData(): void {
    if (!this.reportData || !this.selectedAssessment || !this.selectedProject) return;

    const scoring = this.reportData.data.scoring ?? {};
    const traitScores: Record<string, number> = {};
    for (const trait of TRAITS) {
      const key = `${trait}_bellcurve`;
      if (key in scoring) {
        traitScores[trait] = scoring[key];
      }
    }

    // Assessment list dates are typed as Date but arrive from JSON as ISO strings.
    const selectedDateCompleted = this.fullAssessments.find((a) => a.assessmentId === this.selectedAssessment)?.dateCompleted;
    this.comparisonCandidates = Vue.observable<ComparisonAssessmentItem[]>([
      {
        data: {
          assessmentId: this.selectedAssessment,
          project: this.selectedProject,
          candidateName: this.selectedAssessmentName,
          isComplete: true,
          dateCompleted: selectedDateCompleted ? String(selectedDateCompleted) : undefined,
          readinessScore: this.reportData.data.readiness ?? "default",
          traitScores,
          competencies: {},
          competencyScores: {},
        },
        id: this.selectedAssessment,
        hidden: false,
        highlighted: false,
        locked: true,
        selected: true,
        color: ColorMap[this.projectTheme]?.["default"] ?? "#0b4b70",
        initialColor: ColorMap[this.projectTheme]?.["default"] ?? "#0b4b70",
      },
    ]);

    const rootBenchmarkId = this.fullProjects.find((p) => p.projectId === this.selectedProject)?.rootBenchmarkId;
    this.comparisonBenchmarks = Vue.observable<ComparisonBenchmarkItem[]>(
      this.benchmarks
        .filter((b) => b.benchmarkId === rootBenchmarkId)
        .map((benchmark) => ({
          data: benchmark,
          id: benchmark.benchmarkId,
          hidden: false,
          highlighted: false,
          locked: false,
          selected: true,
          color: benchmark.colour,
          initialColor: benchmark.colour,
        })),
    );
  }

  async publish(): Promise<void> {
    if (!this.selectedAssessment || !this.reportData) return;
    this.publishing = true;

    const reportData = this.reportData.data as ElraReportData;
    reportData.reportVersion = this.reportVersion;
    reportData.type = this.reportType;

    try {
      const result = await adminService.publishReport(this.selectedAssessment, reportData, this.sendEmail, this.reportData.readiness === "beta");
      if (result.success) {
        alert("Report published!");
      } else {
        alert(`Error publishing report: ${result.detail}`);
      }
    } catch {
      this.errorMsgs.push("Error publishing report");
    } finally {
      this.selectedAssessment = null;
      this.publishing = false;
      this.sendEmail = false;
      this.$emit("published");
    }
  }
}
